/**
 * @fileoverview Microsoft drive provider constants + account-status hook
 * (MOR-555 Work OS Sync). Non-component exports live here so the component
 * files stay fast-refresh friendly.
 */

import { useQuery } from "@tanstack/react-query";
import { useAction } from "convex/react";
import { api } from "@cvx/_generated/api";
import oneDriveLogo from "@/assets/onedrive.svg";
import sharePointLogo from "@/assets/sharepoint.svg";
import xeroLogo from "@/assets/xero.svg";

export type DriveProvider = "microsoft-onedrive" | "microsoft-sharepoint";
/** All providers connectable via WorkOS Pipes (account-level). */
export type PipesProvider = DriveProvider | "xero";

export const DRIVE_PROVIDERS: DriveProvider[] = [
  "microsoft-onedrive",
  "microsoft-sharepoint",
];

export const ACCOUNT_PROVIDERS: PipesProvider[] = [...DRIVE_PROVIDERS, "xero"];

export const PROVIDER_LABELS: Record<PipesProvider, string> = {
  "microsoft-onedrive": "OneDrive",
  "microsoft-sharepoint": "SharePoint",
  xero: "Xero",
};

export const PROVIDER_LOGOS: Record<PipesProvider, string> = {
  "microsoft-onedrive": oneDriveLogo,
  "microsoft-sharepoint": sharePointLogo,
  xero: xeroLogo,
};

export type AccountStatus =
  | "connected"
  | "not_connected"
  | "needs_reauthorization"
  | "provider_not_configured";

export type AccountInfo = { status: AccountStatus; account?: string };

export function providerNotConfiguredMessage(provider: PipesProvider): string {
  return `${PROVIDER_LABELS[provider]} is not enabled in the WorkOS dashboard yet. Enable the "${provider}" provider under Pipes, then try again.`;
}

/** Backend formats the account as "email (work account)" — split for display. */
export function parseAccount(account: string): {
  email: string;
  kind: string | null;
} {
  const match = /^(.*?)\s*\((work|personal) account\)$/i.exec(account);
  if (!match) return { email: account, kind: null };
  return {
    email: match[1],
    kind:
      match[2].toLowerCase() === "work" ? "Work account" : "Personal account",
  };
}

/** Account-level provider statuses, shared across all surfaces. */
export function useProviderAccountStatuses() {
  const getStatuses = useAction(
    api.integrations.workOsSync.browseActions.getProviderAccountStatuses,
  );
  return useQuery({
    queryKey: ["pipes-account-statuses"],
    queryFn: () => getStatuses({}),
    staleTime: 30_000,
  });
}
